import { Button, SettingsRow } from '@wundo/ui';
import type { IntegrationProvider } from '@/lib/integrations.ts';
import { m } from '@/paraglide/messages.js';
import type {
  CalendarAccount,
  CalendarConnections,
  TestResult,
} from './useCalendarConnections.ts';

interface CalendarConnectionRowProps {
  provider: IntegrationProvider;
  /** The provider's name, as the row's label. */
  name: string;
  connections: CalendarConnections;
  /** Asks before disconnecting: the page holds the one confirm dialog. */
  onDisconnect: (account: CalendarAccount) => void;
}

/** One calendar provider: whether it is connected, and what can be done about it. */
export function CalendarConnectionRow({
  provider,
  name,
  connections,
  onDisconnect,
}: CalendarConnectionRowProps) {
  const accountId = connections.accountIdOf(provider);
  const testResult = connections.resultOf(provider);
  const busy = connections.busy;
  const onConnect = () => connections.connect(provider);

  return (
    <SettingsRow label={name}>
      {/*
       * Two buttons at most. Testing is the diagnostic and reconnecting the
       * remedy, so reconnect takes the test's slot once a test has failed.
       */}
      <div className='flex flex-wrap items-center gap-2'>
        {/*
         * On the buttons' line rather than under the name: text is shorter than
         * a button, so the row keeps its height whether or not a test answered,
         * and nothing has to reserve a line that would lift the name off centre.
         */}
        <TestMessage testing={connections.testing(provider)} result={testResult} />

        {accountId !== null ? (
          <>
            {testResult === false ? (
              <Button size='sm' variant='quiet' disabled={busy} onClick={onConnect}>
                {m.connections_reconnect()}
              </Button>
            ) : (
              <Button
                size='sm'
                variant='quiet'
                disabled={busy || connections.testRunning}
                onClick={() => connections.test(provider)}
              >
                {m.connections_test()}
              </Button>
            )}
            <Button
              size='sm'
              variant='dangerQuiet'
              disabled={busy}
              onClick={() => onDisconnect({ provider, accountId })}
            >
              {m.connections_disconnect()}
            </Button>
          </>
        ) : (
          <Button size='sm' disabled={busy} onClick={onConnect}>
            {m.connections_connect()}
          </Button>
        )}
      </div>
    </SettingsRow>
  );
}

/*
 * Says nothing until a test has said something. `aria-live`, because pressing
 * Testen answers nowhere but this line, so a screen reader has to be told.
 */
function TestMessage({ testing, result }: { testing: boolean; result: TestResult }) {
  return (
    <span role='status' aria-live='polite' className='text-sm text-ink-muted'>
      <TestText testing={testing} result={result} />
    </span>
  );
}

function TestText({ testing, result }: { testing: boolean; result: TestResult }) {
  if (testing) return <>{m.connections_testing()}</>;
  if (result === true) {
    return <span className='text-success'>{m.connections_test_ok()}</span>;
  }
  /* The one case worth colouring red: granted once, and no longer answering. */
  if (result === false) {
    return <span className='text-danger'>{m.connections_test_failed()}</span>;
  }

  return null;
}
